import { useMemo } from 'react';

import { useFormikContext } from 'formik';
import { get, camelCase } from 'lodash';
import { Col, Form } from 'react-bootstrap';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

import 'react-phone-number-input/style.css';

import type { IForm, IFormField } from '@customTypes/form';
import { camelToTitleCase } from '@helpers/string';

const generateName = (name: string, prefix = ''): string => {
  return prefix ? `${prefix}.${name}` : name;
};

export default function PhoneField<T extends IForm>({
  prefix = '',
  name,
  cols,
  className = '',
  label,
  disabled = false,
  required = false,
  helperText = '',
}: Omit<IFormField<T>, 'type' | 'address' | 'inputProps'>) {
  const { touched, errors, values, handleBlur, setFieldValue } = useFormikContext<T>();

  const groupClassName = useMemo(() => {
    if (cols === 4) {
      return 'col-12 col-lg-4';
    }
    if (cols === 8) {
      return 'col-12 col-lg-8';
    }
    return className;
  }, [cols, className]);

  const formControlName = useMemo(() => generateName(name, prefix), [prefix, name]);
  const error = useMemo(() => get(errors, formControlName), [formControlName, errors]);
  const isDirty = useMemo(() => get(touched, formControlName), [formControlName, touched]);
  const fieldValue = useMemo(() => get(values, formControlName), [values, formControlName]);
  const isInvalid = useMemo(() => {
    const value = fieldValue as string | undefined;
    return !!value && !isValidPhoneNumber(value);
  }, [fieldValue]);

  const displayError = useMemo(() => {
    if (isDirty && error) return error as string;
    if (isDirty && isInvalid) return 'Please provide a valid phone number.';
    return null;
  }, [error, isDirty, isInvalid]);

  const handleChange = (value: string | undefined) => {
    setFieldValue(formControlName, value ?? '');
  };

  const handleBlurEvent = () => {
    handleBlur({ target: { name: formControlName } } as any);
  };

  return (
    <Form.Group as={Col} controlId={`input_${formControlName}`} className={groupClassName}>
      <Form.Label className={required ? 'required' : ''}>{label ?? camelToTitleCase(camelCase(name))}</Form.Label>
      <PhoneInput
        id={`input_${formControlName}`}
        name={formControlName}
        disabled={disabled}
        required={required}
        placeholder={helperText || undefined}
        numberInputProps={{
          className: `form-control ${displayError ? 'is-invalid' : ''} ${isDirty && !displayError ? 'is-valid' : ''}`,
        }}
        onBlur={handleBlurEvent}
        defaultCountry='US'
        value={fieldValue as string | undefined}
        onChange={handleChange}
        aria-invalid={!!displayError}
        aria-describedby={helperText ? `input_${formControlName}Desc` : undefined}
      />
      {displayError && (
        <div className='invalid-feedback d-block' role='alert'>
          {displayError}
        </div>
      )}
    </Form.Group>
  );
}
